import { useCallback, useEffect, useState } from "react";

/**
 * Tâche 1166 (Eric 2026-06-17) — Mémoire des familles de parcours repliées.
 *
 * Replier « Formations hebdomadaires » sur un fichier puis la retrouver
 * dépliée sur le suivant, c'est refaire le même geste à chaque fichier. Le
 * choix est donc gardé dans le navigateur (préférence d'affichage, propre à
 * la personne : rien à stocker côté serveur, rien à synchroniser).
 *
 * Un SEUL hook pour les deux menus (fiche du fichier et liste de
 * l'explorateur) : deux copies finiraient par mémoriser deux états
 * différents, et replier d'un côté ne se verrait pas de l'autre.
 */
const STORAGE_KEY = "knoria.pathways.collapsedGroups";

const readStored = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((k) => typeof k === "string") : []);
  } catch {
    // localStorage indisponible (navigation privée, quota) : on repart
    // déplié plutôt que de casser le menu.
    return new Set();
  }
};

export const useCollapsedPathGroups = () => {
  const [collapsed, setCollapsed] = useState(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify([...collapsed]));
    } catch {
      // Rien à faire : une préférence d'affichage non gardée n'est pas une
      // erreur à remonter à l'utilisateur.
    }
  }, [collapsed]);

  const toggleGroup = useCallback((key) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  return { collapsed, toggleGroup };
};

export { STORAGE_KEY as COLLAPSED_GROUPS_STORAGE_KEY };
export default useCollapsedPathGroups;
